import type { PropsWithChildren } from 'react';
import React from 'react';
import { format } from 'date-fns';
import millify from 'millify';
import { twMerge } from 'tailwind-merge';
import type { FragmentType, TierNft } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { RankCardFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import type { AudienceSortType } from './constants';

type RankType = 'vip' | 'goldenEgg' | 'normal';

const RANK_MAX = 3;

export function showRank({ rank }: { rank: number }) {
  return rank < RANK_MAX;
}

function RankContainer({ children }: PropsWithChildren) {
  return (
    <div
      className={twMerge(
        'absolute bottom-3 mx-2 flex w-[calc(100%-24px)] items-center',
        'gap-2 rounded-[6px] bg-[rgba(0,0,0,0.7)] p-2 backdrop-blur-md',
      )}
    >
      {children}
    </div>
  );
}

function Rank({
  hasDecorators,
  type,
  rank,
}: {
  hasDecorators: boolean;
  type: RankType;
  rank: number;
}) {
  return (
    <div className="hidden items-center justify-center md:flex">
      <div
        className={twMerge(
          'flex w-4 items-center justify-center font-title text-title-xxs font-bold',
          'text-white md:w-8 md:text-title-xl md:font-medium',
          hasDecorators && 'text-gradient-animated bg-clip-text',
          type === 'goldenEgg' ? 'golden-egg-text' : type === 'vip' ? 'limited-text' : '',
        )}
        style={{
          WebkitTextFillColor: hasDecorators ? 'transparent' : 'initial',
        }}
      >
        {rank}
      </div>
    </div>
  );
}

export function RankCard({
  rankFrag,
  rank,
  type,
  tier,
  sort,
}: {
  rankFrag: FragmentType<RankCardFragmentDoc>;
  rank: number;
  type: RankType;
  tier: TierNft;
  sort: AudienceSortType;
}) {
  const {
    nftsCount,
    gaNftsCount,
    vipNftsCount,
    user: { username },
    firstNftCollectedDate,
  } = getFragment(RankCardFragmentDoc, rankFrag);

  const count = tier === 'ALL' ? nftsCount : tier === 'VIP' ? vipNftsCount : gaNftsCount;

  const hasDecorators = type !== 'normal';

  return (
    <RankContainer>
      <Rank hasDecorators={type !== 'normal'} rank={rank} type={type} />
      <div className="flex flex-col gap-[2px] overflow-hidden md:gap-0">
        <h2 className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-s font-semibold text-white md:text-base-m md:font-bold">
          {username}
        </h2>
        <p
          className={twMerge(
            'font-base text-[11px] font-normal leading-[9px] text-white md:text-base-s md:font-medium',
            hasDecorators && 'text-gradient-animated bg-clip-text',
            type === 'goldenEgg'
              ? 'golden-egg-text'
              : type === 'vip'
              ? 'limited-text'
              : 'text-base400',
          )}
          style={{
            WebkitTextFillColor: hasDecorators ? 'transparent' : 'initial',
          }}
        >
          {sort === 'top' ? (
            <>
              {millify(count)}
              {SPACE}
              {getSingularOrPlural({
                word: 'edition',
                count,
              })}
            </>
          ) : (
            format(new Date(firstNftCollectedDate), 'P')
          )}
        </p>
      </div>
    </RankContainer>
  );
}

export function EmptyRankCard({ rank, type }: { rank: number; type: RankType }) {
  return (
    <RankContainer>
      <Rank hasDecorators={type !== 'normal'} rank={rank} type={type} />
      <div className="flex flex-col overflow-hidden">
        <h2 className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-m font-bold text-white">
          Collect to join
        </h2>
      </div>
    </RankContainer>
  );
}
